import React from 'react';
import { Paper, Typography, useTheme } from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import EcoIcon from '@mui/icons-material/EnergySavingsLeaf';
import { Loader, Title, ReloadIconButton } from '../../elements';
import { LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, notifications } from '../../utils';
import { HealthSystemDomainCarbonFootPrintResultCarbonFootprintPerYearViewModel } from '../../api/HealthSystemApi';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { ApiService } from '../../hooks';

const getYearRange = (date: Date) => ({
  from: new Date(date.getFullYear(), 0, 1),
  to: new Date(date.getFullYear(), 11, 31)
});

const CarbonFootprint: React.FC = () => {
  const { t } = useTranslation();
  const styles = sxStyles(useTheme());

  const apiService = new ApiService();
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const { from: defaultFrom, to: defaultTo } = getYearRange(new Date());
  const [fromDate, setFromDate] = React.useState<Date>(defaultFrom);
  const [toDate, setToDate] = React.useState<Date>(defaultTo);
  const [carbonData, setCarbonData] =
    React.useState<HealthSystemDomainCarbonFootPrintResultCarbonFootprintPerYearViewModel | null>(null);

  const loadCarbonData = async (from: Date, to: Date) => {
    setIsBusy(true);

    apiService.api.carbonFootprint
      .carbonFootprintGetCarbonFootprintByYear({ From: format(from, 'yyyy-MM-dd'), To: format(to, 'yyyy-MM-dd') })
      .then((result) => {
        setCarbonData(result.data);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (!loaded) {
      loadCarbonData(fromDate, toDate);
    }
  }, [loaded]);

  const handleFromDateChange = (value: unknown) => {
    const date = value as Date | null;
    if (date) {
      setFromDate(date);
      loadCarbonData(date, toDate);
    }
  };

  const handleToDateChange = (value: unknown) => {
    const date = value as Date | null;
    if (date) {
      setToDate(date);
      loadCarbonData(fromDate, date);
    }
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={12}>
        <Grid container direction="row" sx={styles.paperHeader}>
          <Grid item container md={6} xs={10} spacing={1}>
            <Grid item>
              <EcoIcon color="primary" fontSize="large" />
            </Grid>
            <Grid item>
              <Title>{t('f-carbon-footprint')}</Title>
            </Grid>
          </Grid>
          <Grid item container sx={styles.justifyRight} md={6} xs={10}>
            <Grid item>
              <ReloadIconButton action={() => setLoaded(false)} />
            </Grid>
          </Grid>
        </Grid>

        <Grid container item xs={12} spacing={1}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid item style={{ minWidth: 180 }}>
              <DatePicker
                format="dd/MM/yyyy"
                label={t('m-search-date-from')}
                value={fromDate}
                onChange={handleFromDateChange}
                minDate={new Date(2020, 0)}
                maxDate={toDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
            <Grid item style={{ minWidth: 180 }}>
              <DatePicker
                format="dd/MM/yyyy"
                label={t('m-search-date-to')}
                value={toDate}
                onChange={handleToDateChange}
                minDate={fromDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Grid>
          </LocalizationProvider>
        </Grid>
        {isBusy ? (
          <Grid item xs={12}>
            <Loader isLoading={true} size={LoaderSize.DEFAULT_SIZE} />
          </Grid>
        ) : carbonData ? (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item md={6}>
                <Paper style={{ padding: 16 }}>
                  <Typography variant="h6">{t('f-carbon-total')}</Typography>
                  <Typography variant="body1">{(carbonData.totalKgCo2e ?? 0).toLocaleString()} kg CO₂e</Typography>
                </Paper>
              </Grid>
              <Grid item md={6}>
                <Paper style={{ padding: 16 }}>
                  <Typography variant="h6">{t('f-carbon-per-kg')}</Typography>
                  <Typography variant="body1">
                    {(carbonData.kgCo2ePerKgMeat ?? 0).toFixed(2)} kg CO₂e / kg meat
                  </Typography>
                </Paper>
              </Grid>
              <Grid item md={6}>
                <Paper style={{ padding: 16 }}>
                  <Typography variant="h6">{t('f-carbon-feed-co2')}</Typography>
                  <Typography variant="body1">{(carbonData.totalFeedCo2 ?? 0).toLocaleString()} kg CO₂e</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Typography variant="body1" color="textSecondary">
              {t('f-carbon-not-calculated')}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default CarbonFootprint;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    },
    resultsTable: {
      marginTop: theme.spacing(2)
    }
  }) as const;
